/**
 * Assets panel, a dock tab of the Chart workspace (W2-10 projects-data; DESIGN.md §4.18).
 *
 * Every file of the chart with its role, size and users ("used by 2 lines and 31 notes"), problems Phira would have
 * with it (missing, not decodable by Phira, larger than 2048 px, names Phira mishandles) and its actions: Replace,
 * Rename, Download, Downscale (large images), Remove (asks when the file is used). Unused files are listed apart with
 * "Remove all unused". "Add file" adds files with sanitised names. The naming rules are spelled out at the bottom.
 */

import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { PanelProps } from '../../app/registry/types';
import type { AssetId } from '../../core/model/types';
import type { BadgeProps } from '../../ui';
import { Badge, Button, EmptyState, Icon, IconButton } from '../../ui';
import { m } from './assets.messages';
import {
  downloadAsset,
  downscaleAsset,
  pickAndAdd,
  removeAsset,
  removeUnused,
  renameAsset,
  replaceAsset,
} from './assetActions';
import {
  assetProblems,
  assetRows,
  canDownscale,
  ROLE_ICONS,
  ROLE_TITLES,
  usageText,
  type AssetProblem,
  type AssetRow,
} from './assetModel';
import { formatBytes } from './format';
import { useMissingAssets } from './presence';
import { assetsUi, useRenaming } from './assetsUi';
import { useAssetsVersion, useServicesStore, useText, type AssetsText } from './hooks';
import s from './Assets.module.css';

function problemBadge(p: AssetProblem): BadgeProps {
  switch (p.kind) {
    case 'missing':
      return {
        kind: 'problem',
        severity: 'fatal',
        label: { key: m.badgeMissing },
        tooltip: { key: m.badgeMissingTip },
      };
    case 'undecodable':
      return {
        kind: 'problem',
        severity: 'fatal',
        label: { key: m.badgeUndecodable },
        tooltip: { key: m.badgeUndecodableTip, params: { codec: p.codec === '' ? '?' : p.codec } },
      };
    case 'badName':
      return {
        kind: 'problem',
        severity: 'error',
        label: { key: m.badgeName },
        tooltip: {
          key:
            p.reason === 'builtinHitsound'
              ? m.nameBuiltinHitsound
              : p.reason === 'clickPng'
                ? m.nameClickPng
                : p.reason === 'reserved'
                  ? m.nameReserved
                  : m.nameInvalid,
        },
      };
    case 'nonAscii':
      return {
        kind: 'problem',
        severity: 'tip',
        label: { key: m.badgeNonAscii },
        tooltip: { key: m.badgeNonAsciiTip },
      };
    case 'large':
      return {
        kind: 'problem',
        severity: 'tip',
        label: { key: m.badgeLarge, params: { width: p.width, height: p.height } },
        tooltip: { key: m.badgeLargeTip },
      };
  }
}

function RenameInput(props: {
  readonly text: AssetsText;
  readonly initial: string;
  readonly onDone: (v: string | null) => void;
}) {
  const { text, initial, onDone } = props;
  const [value, setValue] = useState(initial);
  const input = useRef<HTMLInputElement>(null);
  const finished = useRef(false);
  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, []);
  const finish = (v: string | null): void => {
    if (finished.current) return;
    finished.current = true;
    onDone(v);
  };
  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      finish(value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      finish(null);
    }
  };
  return (
    <label className={s.renameField}>
      <span className="visually-hidden">{text.t(m.renameLabel)}</span>
      <input
        ref={input}
        className={s.renameInput}
        value={value}
        spellCheck={false}
        onChange={(e) => {
          setValue(e.target.value);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          finish(value);
        }}
      />
    </label>
  );
}

function AssetRowView(props: {
  readonly services: PanelProps['services'];
  readonly text: AssetsText;
  readonly row: AssetRow;
  readonly renaming: boolean;
  readonly onRename: (id: AssetId | null) => void;
  /** The browser lost the file's bytes (UX-066). */
  readonly storageMissing: boolean;
}) {
  const { services, text, row, renaming, storageMissing } = props;
  const { ref, usage } = row;
  const problems = assetProblems(row);
  const missing = ref.missing === true || storageMissing;
  const dims =
    ref.meta?.width !== undefined && ref.meta.height !== undefined
      ? text.t(m.dimensions, { width: ref.meta.width, height: ref.meta.height })
      : null;
  return (
    <li className={s.row} data-missing={missing || undefined} data-asset-id={ref.id}>
      <Icon name={ROLE_ICONS[ref.role]} size={16} />
      <span className={s.main}>
        {renaming ? (
          <RenameInput
            text={text}
            initial={ref.path}
            onDone={(v) => {
              props.onRename(null);
              if (v !== null) renameAsset(services, ref.id, v);
            }}
          />
        ) : (
          <span className={s.path} title={ref.path}>
            {ref.path}
          </span>
        )}
        <span className={s.meta}>
          <span className={s.role}>{text.t(ROLE_TITLES[ref.role])}</span>
          <span>{formatBytes(ref.bytes, text)}</span>
          {dims !== null && <span>{dims}</span>}
          <span className={s.usage} data-unused={row.uses === 0 || undefined}>
            {usageText(text, usage)}
          </span>
        </span>
        {(problems.length > 0 || storageMissing) && (
          <span className={s.badges}>
            {storageMissing && (
              <Badge
                kind="problem"
                severity="fatal"
                label={{ key: m.badgeStorageMissing }}
                tooltip={{ key: m.badgeStorageMissingTip }}
              />
            )}
            {problems.map((p) => (
              <Badge key={p.kind} {...problemBadge(p)} />
            ))}
          </span>
        )}
      </span>
      <span className={s.actions}>
        {missing ? (
          <Button
            size="sm"
            variant="primary"
            icon="FolderOpen"
            onClick={() => {
              void replaceAsset(services, ref.id);
            }}
          >
            {text.t(m.chooseFile)}
          </Button>
        ) : (
          <IconButton
            icon="Replace"
            size="sm"
            label={{ key: m.replaceLabel, params: { name: ref.path } }}
            onClick={() => {
              void replaceAsset(services, ref.id);
            }}
          />
        )}
        <IconButton
          icon="Pencil"
          size="sm"
          label={{ key: m.renameAction, params: { name: ref.path } }}
          onClick={() => {
            props.onRename(ref.id);
          }}
        />
        {canDownscale(ref) && (
          <IconButton
            icon="ImageDown"
            size="sm"
            label={{ key: m.downscaleLabel, params: { name: ref.path } }}
            onClick={() => {
              void downscaleAsset(services, ref.id);
            }}
          />
        )}
        <IconButton
          icon="Download"
          size="sm"
          label={{ key: m.downloadLabel, params: { name: ref.path } }}
          disabledReason={missing ? { key: m.missingNoDownload } : null}
          onClick={() => {
            void downloadAsset(services, ref.id);
          }}
        />
        <IconButton
          icon="Trash2"
          size="sm"
          label={{ key: m.removeLabel, params: { name: ref.path } }}
          onClick={() => {
            void removeAsset(services, ref.id);
          }}
        />
      </span>
    </li>
  );
}

export function AssetsPanel(props: PanelProps) {
  const { services } = props;
  const text = useText(services);
  const store = useServicesStore(services);
  // Re-render when the files or their users change.
  useAssetsVersion(store);
  const renaming = useRenaming();

  const rows = store === null ? [] : assetRows(store.doc, store.idx);
  const lost = useMissingAssets(
    services.persist,
    rows.filter((r) => r.ref.missing !== true).map((r) => r.ref.id),
  );
  const used = rows.filter((r) => r.uses > 0);
  const unused = rows.filter((r) => r.uses === 0);
  const total = rows.reduce((sum, r) => sum + r.ref.bytes, 0);

  if (store === null) {
    return (
      <div className={s.panel} data-region="assets">
        <EmptyState icon="Files" title={{ key: m.noProjectTitle }} body={{ key: m.noProjectBody }} />
      </div>
    );
  }

  const list = (items: readonly AssetRow[], label: string) => (
    <ul className={s.list} aria-label={label}>
      {items.map((row) => (
        <AssetRowView
          key={row.ref.id}
          services={services}
          text={text}
          row={row}
          renaming={renaming === row.ref.id}
          storageMissing={lost.has(row.ref.id)}
          onRename={(id) => {
            assetsUi.setRenaming(id);
          }}
        />
      ))}
    </ul>
  );

  return (
    <div className={s.panel} data-region="assets">
      <div className={s.header}>
        <span className={s.summary}>{text.t(m.summary, { count: rows.length, size: formatBytes(total, text) })}</span>
        <Button
          size="sm"
          icon="Upload"
          onClick={() => {
            void pickAndAdd(services, null);
          }}
        >
          {text.t(m.addFile)}
        </Button>
      </div>
      <div className={s.body}>
        {rows.length === 0 ? (
          <EmptyState icon="Files" title={{ key: m.emptyTitle }} body={{ key: m.emptyBody }} />
        ) : (
          <>
            {used.length > 0 && list(used, text.t(m.usedListLabel))}
            {unused.length > 0 && (
              <section className={s.unused} aria-labelledby="assets-unused-title">
                <div className={s.unusedHeader}>
                  <h3 id="assets-unused-title" className={s.h3}>
                    {text.t(m.unusedTitle, { count: unused.length })}
                  </h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="Trash2"
                    onClick={() => {
                      void removeUnused(services);
                    }}
                  >
                    {text.t(m.removeAllUnused)}
                  </Button>
                </div>
                {list(unused, text.t(m.unusedListLabel))}
              </section>
            )}
          </>
        )}
      </div>
      <p className={s.rules}>
        <Icon name="Info" size={14} />
        <span>{text.t(m.namingRules)}</span>
      </p>
    </div>
  );
}
